import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from "axios";

export interface AuthTokenProvider {
  getAccessToken: () => string | null;
  onRefreshSuccess: (newAccessToken: string) => void;
  onUnauthenticated: () => void;
  refresh: () => Promise<string>;
}

interface AuthRetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _authHeaderSet?: boolean;
}

let tokenProvider: AuthTokenProvider | null = null;

export function installAuthTokenProvider(provider: AuthTokenProvider): void {
  tokenProvider = provider;
}

export const api = axios.create({
  baseURL: "/api",
  withCredentials: true,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const cfg = config as AuthRetryConfig;
  if (cfg._authHeaderSet) {
    return cfg;
  }
  const token = tokenProvider?.getAccessToken();
  if (token) {
    cfg.headers.set("Authorization", `Bearer ${token}`);
  }
  return cfg;
});

let refreshInFlight: Promise<string> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const originalReq = error.config as AuthRetryConfig | undefined;
    if (!originalReq || !error.response) throw error;
    if (error.response.status !== 401) throw error;
    if (originalReq._retried) throw error;
    const url = originalReq.url ?? "";
    if (url.startsWith("/auth/login") || url.startsWith("/auth/refresh")) {
      throw error;
    }
    if (!tokenProvider) throw error;

    try {
      if (!refreshInFlight) {
        refreshInFlight = tokenProvider.refresh().finally(() => {
          refreshInFlight = null;
        });
      }
      const newToken = await refreshInFlight;
      originalReq._retried = true;
      originalReq._authHeaderSet = true;
      originalReq.headers.set("Authorization", `Bearer ${newToken}`);
      return api.request(originalReq as AxiosRequestConfig);
    } catch (refreshErr) {
      tokenProvider.onUnauthenticated();
      throw refreshErr;
    }
  },
);
